import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heart, ShoppingCart, Star, Share2, ChevronLeft, CheckCircle, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, size = 'w-4 h-4', interactive = false, onRate }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`${size} ${star <= rating ? 'text-royal-gold fill-royal-gold' : 'text-[#E0D5D0]'} ${interactive ? 'cursor-pointer hover:text-royal-gold' : ''}`}
        onClick={() => interactive && onRate?.(star)}
      />
    ))}
  </div>
);

const ShareButtons = ({ product }) => {
  const url = `${window.location.origin}/products/${product.id}`;
  const text = `Check out ${product.name} from The Perfect Peach Club!`;

  const share = (platform) => {
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };

    if (platform === 'copy' || platform === 'instagram') {
      navigator.clipboard.writeText(url);
      toast.success(platform === 'instagram' ? 'Link copied! Paste it on Instagram' : 'Link copied!');
      return;
    }
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={() => share('whatsapp')} className="w-9 h-9 rounded-full bg-[#25D366]/10 flex items-center justify-center hover:bg-[#25D366]/20 transition-colors" data-testid="share-whatsapp" title="Share on WhatsApp">
        <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </button>
      <button onClick={() => share('facebook')} className="w-9 h-9 rounded-full bg-[#1877F2]/10 flex items-center justify-center hover:bg-[#1877F2]/20 transition-colors" data-testid="share-facebook" title="Share on Facebook">
        <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </button>
      <button onClick={() => share('instagram')} className="w-9 h-9 rounded-full bg-[#E4405F]/10 flex items-center justify-center hover:bg-[#E4405F]/20 transition-colors" data-testid="share-instagram" title="Copy link for Instagram">
        <svg className="w-4 h-4 text-[#E4405F]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
      </button>
      <button onClick={() => share('copy')} className="w-9 h-9 rounded-full bg-african-earth/10 flex items-center justify-center hover:bg-african-earth/20 transition-colors" data-testid="share-copy" title="Copy link">
        <Share2 className="w-4 h-4 text-african-earth" />
      </button>
    </div>
  );
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], average_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [variants, setVariants] = useState([]);

  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewOrderId, setReviewOrderId] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/reviews/${id}`)
        ]);
        const found = productRes.data.find(p => p.id === id);
        setProduct(found || null);
        setReviews(reviewsRes.data);

        // Find variants (same variant_group, different ID)
        if (found?.variant_group) {
          const siblings = productRes.data.filter(p => p.variant_group === found.variant_group && p.id !== id);
          setVariants(siblings);
        } else {
          setVariants([]);
        }

        const wishlist = JSON.parse(localStorage.getItem('ppc_wishlist') || '[]');
        setIsWishlisted(wishlist.some(p => p.id === id));
      } catch {
        // Ignore
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const toggleWishlist = () => {
    const wishlist = JSON.parse(localStorage.getItem('ppc_wishlist') || '[]');
    if (isWishlisted) {
      const updated = wishlist.filter(p => p.id !== product.id);
      localStorage.setItem('ppc_wishlist', JSON.stringify(updated));
      setIsWishlisted(false);
      toast.success('Removed from wishlist');
    } else {
      wishlist.push({ id: product.id, name: product.name, price: product.price, image_url: product.image_url });
      localStorage.setItem('ppc_wishlist', JSON.stringify(wishlist));
      setIsWishlisted(true);
      toast.success('Added to wishlist');
    }
  };

  const handleAddToCart = () => {
    addToCart(product);
    toast.success('Added to cart');
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: id,
        email: reviewEmail,
        order_id: reviewOrderId,
        name: reviewName,
        rating: reviewRating,
        text: reviewText,
      });
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setReviewEmail(''); setReviewOrderId(''); setReviewName(''); setReviewRating(5); setReviewText('');
      const res = await axios.get(`${API}/reviews/${id}`);
      setReviews(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-perfect-peach" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="text-center pt-32 px-5">
          <h1 className="font-playfair text-2xl text-[#2D2420] mb-4">Product not found</h1>
          <Link to="/products" className="text-perfect-peach hover:underline">Back to products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{product.name} | The Perfect Peach Club</title>
        <meta name="description" content={product.description} />
      </Helmet>
      <Navigation />
      <ChatWidget />

      <div className="max-w-6xl mx-auto px-5 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
        <Link to="/products" className="inline-flex items-center gap-1 text-african-earth hover:text-perfect-peach text-sm mb-6 transition-colors" data-testid="back-to-products">
          <ChevronLeft className="w-4 h-4" /> Back to products
        </Link>

        {/* Product Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12">
          <div className="relative rounded-2xl overflow-hidden">
            <img src={product.image_url} alt={product.name} className="w-full aspect-square object-cover" />
            <button
              onClick={toggleWishlist}
              className="absolute top-4 right-4 w-11 h-11 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:scale-105 transition-transform"
              data-testid="product-wishlist-toggle"
            >
              <Heart className={`w-5 h-5 ${isWishlisted ? 'text-red-500 fill-red-500' : 'text-african-earth'}`} />
            </button>
          </div>

          <div className="flex flex-col justify-center">
            <span className="text-xs uppercase tracking-widest text-perfect-peach font-medium mb-2">{product.category}</span>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-[#2D2420] mb-3" data-testid="product-detail-name">
              {product.name}
            </h1>

            {/* Variant Selector */}
            {variants.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="variant-selector">
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-perfect-peach text-white">
                  {product.variant_name}
                </span>
                {variants.map(v => (
                  <Link
                    key={v.id}
                    to={`/product/${v.id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#E0D5D0] text-african-earth hover:border-perfect-peach hover:text-perfect-peach transition-colors"
                    data-testid={`variant-link-${v.id}`}
                  >
                    {v.variant_name} · R{v.price?.toFixed(0)}
                  </Link>
                ))}
              </div>
            )}

            {reviews.total_reviews > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <StarRating rating={Math.round(reviews.average_rating)} />
                <span className="text-sm text-african-earth">
                  {reviews.average_rating} ({reviews.total_reviews} review{reviews.total_reviews !== 1 ? 's' : ''})
                </span>
              </div>
            )}

            <p className="text-african-earth text-base leading-relaxed mb-4">{product.description}</p>
            {product.usage && <p className="text-african-earth/70 text-sm italic mb-4">{product.usage}</p>}

            {/* What it does */}
            {product.what_it_does && (
              <div className="bg-bone-white rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-[#2D2420] mb-1">What it does</p>
                <p className="text-sm text-african-earth leading-relaxed">{product.what_it_does}</p>
              </div>
            )}

            {/* Bundle includes */}
            {product.bundle_includes && (
              <div className="bg-bone-white rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-[#2D2420] mb-2">This set includes</p>
                <ul className="space-y-1">
                  {product.bundle_includes.map((item, i) => (
                    <li key={i} className="text-sm text-african-earth flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-perfect-peach flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-bold text-perfect-peach" data-testid="product-detail-price">R{product.price?.toFixed(2)}</span>
              {product.size && (
                <span className="text-sm text-african-earth/70" data-testid="product-detail-size">· {product.size}</span>
              )}
              {product.original_price && product.original_price > product.price && (
                <>
                  <span className="text-lg text-african-earth/50 line-through">R{product.original_price?.toFixed(2)}</span>
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Save R{product.savings?.toFixed(2)}</span>
                </>
              )}
            </div>

            <p className={`text-sm mb-5 ${product.stock > 5 ? 'text-green-600' : product.stock > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
              {product.stock > 5 ? 'In Stock' : product.stock > 0 ? `Only ${product.stock} left` : 'Out of Stock'}
            </p>

            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className="w-full sm:w-auto bg-perfect-peach text-white py-3.5 px-8 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="product-add-to-cart"
            >
              <ShoppingCart className="w-5 h-5" /> Add to Cart
            </button>

            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-african-earth">Share:</span>
              <ShareButtons product={product} />
            </div>
          </div>
        </div>

        {/* Detailed Product Info */}
        {(product.key_ingredients || product.full_ingredients) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Key Ingredients */}
            {product.key_ingredients && (
              <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="key-ingredients">
                <h3 className="font-playfair text-lg font-semibold text-[#2D2420] mb-3">Key Ingredients</h3>
                <ul className="space-y-2">
                  {product.key_ingredients.map((ing, i) => (
                    <li key={i} className="text-sm text-african-earth flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-perfect-peach flex-shrink-0 mt-1.5" />
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Full Ingredients */}
            {product.full_ingredients && (
              <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="full-ingredients">
                <h3 className="font-playfair text-lg font-semibold text-[#2D2420] mb-3">Full Ingredients</h3>
                <p className="text-sm text-african-earth leading-relaxed">{product.full_ingredients}</p>
              </div>
            )}
          </div>
        )}

        {/* Brand Message */}
        <div className="bg-bone-white rounded-2xl p-6 sm:p-8 mb-8 text-center">
          <p className="text-african-earth text-sm italic leading-relaxed max-w-xl mx-auto">
            "The Perfect Peach Club is about balance, not correction. Gentle care when things feel off, and support when things feel right."
          </p>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-playfair text-2xl font-semibold text-[#2D2420]">
              Reviews {reviews.total_reviews > 0 && `(${reviews.total_reviews})`}
            </h2>
            <button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="text-sm font-medium text-perfect-peach hover:text-[#C57D63] transition-colors flex items-center gap-1"
              data-testid="write-review-button"
            >
              <MessageSquare className="w-4 h-4" /> Write a Review
            </button>
          </div>

          {showReviewForm && (
            <form onSubmit={handleSubmitReview} className="bg-bone-white rounded-xl p-5 mb-6 space-y-4" data-testid="review-form">
              <p className="text-xs text-african-earth/70">Reviews require a verified purchase. Enter the email and order ID from your confirmation email.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="email" value={reviewEmail} onChange={(e) => setReviewEmail(e.target.value)} required placeholder="Your email" className="border border-[#E0D5D0] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach" data-testid="review-email" />
                <input type="text" value={reviewOrderId} onChange={(e) => setReviewOrderId(e.target.value)} required placeholder="Order ID" className="border border-[#E0D5D0] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach" data-testid="review-order-id" />
              </div>
              <input type="text" value={reviewName} onChange={(e) => setReviewName(e.target.value)} required placeholder="Your name" className="w-full border border-[#E0D5D0] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach" data-testid="review-name" />
              <div>
                <label className="block text-sm text-african-earth mb-1.5">Rating</label>
                <StarRating rating={reviewRating} size="w-6 h-6" interactive onRate={setReviewRating} />
              </div>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience (optional)" rows={3} className="w-full border border-[#E0D5D0] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach resize-none" data-testid="review-text" />
              <button type="submit" disabled={submitting} className="bg-perfect-peach text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#C57D63] transition-all disabled:opacity-50" data-testid="submit-review-button">
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          )}

          {reviews.reviews.length === 0 ? (
            <p className="text-african-earth/60 text-sm text-center py-6">No reviews yet. Be the first to review this product!</p>
          ) : (
            <div className="space-y-4">
              {reviews.reviews.map((review) => (
                <div key={review.id} className="border-b border-[#F0EAE6] pb-4 last:border-b-0" data-testid={`review-${review.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-[#2D2420]">{review.name}</span>
                      {review.verified && (
                        <span className="flex items-center gap-0.5 text-[10px] text-green-600 font-medium">
                          <CheckCircle className="w-3 h-3" /> Verified
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-african-earth/50">{new Date(review.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <StarRating rating={review.rating} size="w-3.5 h-3.5" />
                  {review.text && <p className="text-sm text-african-earth mt-2 leading-relaxed">{review.text}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
